import React, { useState, useEffect } from "react";
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';
import { motion } from "framer-motion";
import axios from "axios";
import { useCachedData, useCacheInvalidationListener } from '../utils/useCache';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/backend/php";

function Services() {
  const { data: services, loading, error, refetch } = useCachedData(
    '/api/services',
    () => axios.get(`${API_BASE_URL}/api/services`).then(async (res) => {
      const { normalizeList } = await import('../utils/responseHelpers');
      return normalizeList(res);
    }),
    600000 // 10 minute cache
  );

  // Listen for cache invalidation events from admin pages and refetch
  useCacheInvalidationListener('/api/services', refetch);

  const [featuredAbout, setFeaturedAbout] = useState(null);
  const [featuredImageUrl, setFeaturedImageUrl] = useState(null);
  const [featuredImgError, setFeaturedImgError] = useState(false);

  // Fetch about data on mount
  useEffect(() => {
    let createdImgUrl = null;

    axios.get(`${API_BASE_URL}/api/about`)
      .then(ar => {
        const aboutData = ar.data;
        let found = null;

        // Prefer matching by id=8. Some backends return an array, others a single object.
        if (Array.isArray(aboutData)) {
          found = aboutData.find(item => String(item.id) === '8');
          // Fallback: look for an entry that contains the `what_we_offer` column or the expected title
          if (!found) {
            found = aboutData.find(item => 
              item && (item.hasOwnProperty('what_we_offer') || 
              String((item.title || '')).trim() === 'What We Offer')
            );
          }
        } else if (aboutData) {
          if (String(aboutData.id) === '8') {
            found = aboutData;
          } else if (aboutData.hasOwnProperty('what_we_offer') || aboutData.hasOwnProperty('what_we_offer_url')) {
            found = aboutData;
          }
        }

        setFeaturedAbout(found);

        // Handle image for what_we_offer if present
        if (found) {
          const remoteKey = found.what_we_offer_url || 
                          found.what_we_offer_image || 
                          found.image || 
                          (typeof found.what_we_offer === 'string' && 
                          (found.what_we_offer.startsWith('/') || 
                            found.what_we_offer.startsWith('http') || 
                            found.what_we_offer.startsWith('blob:')) 
                          ? found.what_we_offer 
                          : null);

          if (remoteKey) {
            if (String(remoteKey).startsWith('/api/')) {
              // Fetch blob and create object URL (encode path)
              fetch(`${API_BASE_URL}${encodeURI(remoteKey)}`)
                .then(r => {
                  if (!r.ok) throw new Error('Image fetch failed');
                  return r.blob();
                })
                .then(blob => {
                  createdImgUrl = URL.createObjectURL(blob);
                  setFeaturedImageUrl(createdImgUrl);
                })
                .catch(err => {
                  console.error('Failed to load featured image blob:', err);
                  setFeaturedImgError(true);
                });
            } else {
              const url = String(remoteKey).startsWith('/') 
                ? `${API_BASE_URL}${encodeURI(remoteKey)}` 
                : String(remoteKey);
              setFeaturedImageUrl(encodeURI(url));
            }
          }
        }
      })
      .catch(err => {
        console.error('Failed to fetch about data:', err);
      });

    // Cleanup: revoke created object URL when component unmounts
    return () => {
      if (createdImgUrl) {
        try { 
          URL.revokeObjectURL(createdImgUrl); 
        } catch(e) {
          console.error('Error revoking object URL:', e);
        }
      }
    };
  }, []);

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" }
    })
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-50">
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10 opacity-5">
        <div className="h-full w-full" style={{
          backgroundImage: `
            linear-gradient(rgba(0,33,71,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,33,71,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto">
          {/* Main Title */}
          <motion.h1 
            className="text-5xl sm:text-6xl font-extrabold mb-12 text-center text-black tracking-wide" 
            style={{ textShadow: '0 6px 32px rgba(0,33,71,0.3)' }}
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Our <span className="text-primary-500">Services</span>
          </motion.h1>

          {/* Loading State */}
          {loading && (
            <motion.div 
              className="text-center mb-16 flex justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <LoadingSpinner message="Loading services..." bgColor="bg-transparent" spinnerColor="border-[#73ab40]" py={0} />
            </motion.div>
          )}

          {/* Error State */}
          {error && (
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center px-6 py-3 bg-blue-50 rounded-full shadow-lg border border-blue-200">
                <p className="text-blue-600 font-medium">No services available right now. Check back soon!</p>
              </div>
            </motion.div>
          )}

          {/* Service Categories Summary */}
          {!loading && !error && services && services.length > 0 && (
            <motion.div 
              className="mb-16 w-full max-w-6xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl border border-emerald/20 overflow-hidden px-8 pb-8 p-6 md:p-8 text-center mx-auto">
                <h3 className="text-3xl md:text-4xl font-bold text-center text-navy mb-6">
                  Why Choose Our <span className="text-emerald-400">Services?</span>
                </h3>
                
                <div className="mb-6">
                  <span className="inline-block bg-gradient-to-r from-emerald/15 to-primary/15 rounded-xl px-6 py-4 border border-primary/30">
                    <span className="text-3xl md:text-4xl font-bold text-emerald-400 mr-2 align-middle">{services.length}+</span>
                    <span className="text-base md:text-lg text-navy font-medium align-middle">Professional Services</span>
                  </span>
                </div>

                <p className="text-black text-lg md:text-xl leading-relaxed text-center max-w-3xl mx-auto">
                  We provide comprehensive solutions tailored to your organization's unique needs. 
                  Our experienced team combines <span className="font-semibold text-emerald-400">strategic thinking</span> with{' '}
                  <span className="font-semibold text-primary-500">practical implementation</span> to deliver{' '}
                  <span className="font-semibold text-navy">measurable results</span> that drive sustainable growth and development.
                </p>
              </div>
            </motion.div>
          )}

          {/* Featured content (id=8 from about table) */}
          {featuredAbout && (
            <motion.div 
              className="mb-12 max-w-4xl mx-auto" 
              initial={{ opacity: 0, y: 10 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 md:p-8 text-center mx-auto border border-emerald/20 shadow-lg">
                <h3 className="text-2xl md:text-3xl font-bold text-navy mb-4">
                  {featuredAbout.title || 'What We Offer'}
                </h3>
                
                {(() => {
                  const explicitUrl = featuredImageUrl || 
                                    (featuredAbout && (featuredAbout.what_we_offer_url || 
                                                      featuredAbout.what_we_offer_image || 
                                                      featuredAbout.image));
                  const imageUrl = explicitUrl && String(explicitUrl).startsWith('/') 
                    ? `${API_BASE_URL}${explicitUrl}` 
                    : explicitUrl;

                  if (imageUrl && !featuredImgError) {
                    return (
                      <div className="w-full max-w-3xl mx-auto mb-8">
                        <img
                          src={imageUrl}
                          alt={featuredAbout.title || 'What We Offer'}
                          className="w-full rounded-2xl shadow-lg border border-emerald/30 bg-white"
                          onError={(e) => {
                            console.error('Featured image failed to load:', e.target.src);
                            setFeaturedImgError(true);
                          }}
                          onLoad={() => console.log('Featured image loaded:', imageUrl)}
                        />
                      </div>
                    );
                  }

                  if (featuredImgError && explicitUrl) {
                    return (
                      <div className="w-full max-w-3xl mx-auto mb-8">
                        <div className="rounded-2xl shadow-lg border border-emerald/30 bg-gray-100 p-12 text-center">
                          <div className="text-5xl mb-4">🖼️</div>
                          <p className="text-gray-600 text-lg">Image Not Found</p>
                          <p className="text-gray-500 text-sm mt-2">The image for this section could not be loaded.</p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="text-base md:text-lg text-black leading-relaxed">
                      <SafeRichText 
                        content={featuredAbout.description || 
                                featuredAbout.content || 
                                featuredAbout.body || 
                                featuredAbout.what_we_offer || ''} 
                      />
                    </div>
                  );
                })()}
              </div>
            </motion.div>
          )}

          {/* Services Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {services && services.length > 0 && services.sort((a, b) => (a.id || 0) - (b.id || 0)).map((service, idx) => (
              <motion.div
                key={service.id || idx}
                className="relative bg-white backdrop-blur-sm rounded-3xl pt-10 px-8 pb-8 flex flex-col items-center text-center shadow-xl border-2 border-gray-100 hover:border-primary/40 transition-all duration-500 min-h-[360px] h-full group hover:shadow-2xl"
                custom={idx}
                initial="hidden"
                animate="visible"
                variants={cardVariants}
              >
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-primary/20 to-transparent rounded-bl-3xl rounded-tr-3xl"></div>
                
                {/* Service Title */}
                <h2 className="text-xl md:text-2xl font-bold text-emerald-400 mb-3 drop-shadow-sm group-hover:text-primary-500 transition-colors duration-300">
                  {service.title}
                </h2>

                {/* Service Category */}
                {service.category && (
                  <div className="bg-primary/10 text-primary-500 px-3 py-1 rounded-full font-medium text-base mb-4 border border-primary/30 shadow-sm">
                    {service.category}
                  </div>
                )}

                {/* Service Description */}
                <div className="text-black text-base md:text-lg flex-grow text-center leading-relaxed">
                  <div className="w-full text-left clamped-desc">
                    <SafeRichText content={service.description} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Empty State */}
          {!loading && !error && (!services || services.length === 0) && (
            <motion.div 
              className="text-center py-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8 max-w-md mx-auto">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald to-primary/70 rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
                  <span className="text-2xl">🛠️</span>
                </div>
                <h3 className="text-2xl font-bold text-navy mb-2">No Services Yet</h3>
                <p className="text-navy/70">Services will be displayed here once they are added.</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <style>{`
        /* Multiline clamp with ellipsis for service descriptions */
        .clamped-desc {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-word;
        }

        @media (max-width: 640px) {
          .clamped-desc {
            -webkit-line-clamp: 3;
          }
        }
      `}</style>
    </div>
  );
}

export default Services;